/**
 * highlightElement — Generic DOM utility for scrolling to and temporarily
 * highlighting an element with a visual ring.
 *
 * Used by cross-module reaction flows (e.g. Capital Gate restore after
 * milestone editor close).
 *
 * Dedup: only one highlight per element at a time. Re-calling with the
 * same id resets the timer instead of stacking.
 */

const HIGHLIGHT_CLASS = 'ring-2 ring-accent-primary/50 ring-offset-1';
const FADE_CLASSES = 'transition-shadow duration-700';
const HIGHLIGHT_DURATION_MS = 1500;

/** Active highlights: id → { timerId, classes applied } */
const activeHighlights = new Map<string, { timer: ReturnType<typeof setTimeout>; classes: string[] }>();

/**
 * Scroll to an element and apply a temporary highlight ring.
 *
 * @param id    - DOM element id or `data-highlight-id` value to target
 * @param opts  - Optional config: ringColor override, duration, scroll behavior
 */
export function highlightElement(
    id: string,
    opts?: {
        /** Custom ring class (replaces default primary ring) */
        ringClass?: string;
        /** Duration before fade-out in ms (default: 1500) */
        duration?: number;
        /** ScrollIntoView block option (default: 'nearest') */
        scrollBlock?: ScrollLogicalPosition;
    },
): void {
    const el =
        document.getElementById(id) ??
        document.querySelector(`[data-highlight-id="${id}"]`);

    if (!el) return;

    const duration = opts?.duration ?? HIGHLIGHT_DURATION_MS;
    const ringClass = opts?.ringClass ?? HIGHLIGHT_CLASS;
    const scrollBlock = opts?.scrollBlock ?? 'nearest';

    // ── Dedup: clear previous highlight on same element ────────────────
    const existing = activeHighlights.get(id);
    if (existing) {
        clearTimeout(existing.timer);
        el.classList.remove(...existing.classes);
        activeHighlights.delete(id);
    }

    // Scroll into view
    el.scrollIntoView({ behavior: 'smooth', block: scrollBlock });

    // Apply highlight
    const classes = `${ringClass} ${FADE_CLASSES}`.split(/\s+/);
    el.classList.add(...classes);

    // Track and schedule removal
    const timer = setTimeout(() => {
        el.classList.remove(...classes);
        activeHighlights.delete(id);
    }, duration);

    activeHighlights.set(id, { timer, classes });
}
